import axios, { AxiosResponse } from "axios";
import { ArrayFromEnvelope, configService, ObjectFromEnvelope } from "./config.service";
import {
  Events,
  IDeleteEventsInPeriod,
  IDeleteEventsOnDates,
  IEventsPost,
  IPostRecurrentEvents,
  IPutRecurrentEvents
} from "../models/events";
import { EventParticipations } from "../models/eventParticipations";
import { EventTypes } from "../models/eventtypes";
import { convertDateOnlyToUtc, convertDateTimeToUtc } from "../utils/dateUtils";
import { EventsPostback, RecurrenceErrorPostback } from "../models/eventsPostback-models";

class EventsService {
  async createEvents(events: IEventsPost): Promise<EventsPostback> {
    return new Promise((resolve, reject) => {
      axios
        .post<Events>(configService.personaleOrAdminUrl("events/create_event.json"), {
          create_event: events
        })
        .then((response: AxiosResponse) => {
          resolve(EventsPostback.fromJSON(ObjectFromEnvelope<EventsPostback>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }

  async deleteRecurrentEvents(eventId: number): Promise<EventsPostback> {
    return new Promise((resolve, reject) => {
      axios
        .post<Events>(configService.personaleOrAdminUrl("events/destroy_recurrent_events.json"), {
          id: eventId
        })
        .then((response: AxiosResponse) => {
          resolve(response.data as any);
        })
        .catch((error) => reject(error));
    });
  }

  async deleteEvents(eventId: number): Promise<EventsPostback> {
    return new Promise((resolve, reject) => {
      axios
        .delete<Events>(configService.personaleOrAdminUrl("events/" + eventId + ".json"))
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  }

  async createRecurrentEvents(
    recurrentInfo: IPostRecurrentEvents
  ): Promise<RecurrenceErrorPostback> {
    const postObj = {
      dates: recurrentInfo.recurrent_dates
        .map((d) => {
          return convertDateOnlyToUtc(d);
        })
        .join(",")
    };
    if (recurrentInfo.employees) {
      postObj["employees"] = recurrentInfo.employees;
    }
    if (recurrentInfo.employee_ids) {
      postObj["employee_ids"] = recurrentInfo.employee_ids;
    }
    if (recurrentInfo.event_id) {
      postObj["event_id"] = recurrentInfo.event_id;
    }
    return new Promise((resolve, reject) => {
      axios
        .post<any>(
          configService.personaleOrAdminUrl(
            "events/" + recurrentInfo.event_id + "/recurrent_events.json"
          ),
          postObj
        )
        .then((response: AxiosResponse) => {
          resolve(
            RecurrenceErrorPostback.fromJSON(
              ObjectFromEnvelope<RecurrenceErrorPostback>(response.data)
            )
          );
        })
        .catch((error) => reject(error));
    });
  }

  async updateRecurrentEvents(
    recurrenceInfo: IPutRecurrentEvents
  ): Promise<RecurrenceErrorPostback> {
    const events: IEventsPost = recurrenceInfo.events;
    return new Promise((resolve, reject) => {
      axios
        .post<any>(configService.personaleOrAdminUrl("events/update_recurrent_events.json"), {
          id: events.id,
          event: {
            name: events.name,
            event_type_id: events.event_type_id,
            start_time: events.start_time,
            end_time: events.end_time,
            note: events.note
          },
          dates: recurrenceInfo.dates
            ? recurrenceInfo.dates
                .map((d) => {
                  return convertDateOnlyToUtc(d);
                })
                .join(",")
            : undefined,
          from_date: recurrenceInfo.from_date
            ? convertDateOnlyToUtc(recurrenceInfo.from_date)
            : undefined,
          to_date: recurrenceInfo.to_date
            ? convertDateOnlyToUtc(recurrenceInfo.to_date)
            : undefined,
          changed_timeregistrations: recurrenceInfo.changed_timeregistrations,
          removed_participants: recurrenceInfo.removed_participants,
          added_participants: recurrenceInfo.added_participants
        })
        .then((response: AxiosResponse) => {
          resolve(
            RecurrenceErrorPostback.fromJSON(
              ObjectFromEnvelope<RecurrenceErrorPostback>(response.data)
            )
          );
        })
        .catch((error) => reject(error));
    });
  }

  async updateEvents(events: IEventsPost): Promise<EventsPostback> {
    return new Promise((resolve, reject) => {
      axios
        .post<Events>(
          configService.personaleOrAdminUrl("events/update_event/" + events.id + ".json"),
          {
            update_event: events
          }
        )
        .then((response: AxiosResponse) => {
          resolve(EventsPostback.fromJSON(ObjectFromEnvelope<EventsPostback>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }

  async deleteRecurrentEventsInPeriod(events: IDeleteEventsInPeriod): Promise<EventsPostback> {
    return new Promise((resolve, reject) => {
      axios
        .post<Events>(
          configService.personaleOrAdminUrl("events/delete_recurrent_events_in_period.json"),
          {
            recurrent_event_id: events.recurrent_event_id,
            from_date: convertDateOnlyToUtc(events.from_date),
            to_date: convertDateOnlyToUtc(events.to_date)
          }
        )
        .then((response: AxiosResponse) => {
          resolve(response.data as any);
        })
        .catch((error) => reject(error));
    });
  }

  async deleteRecurrentEventsOnDates(events: IDeleteEventsOnDates): Promise<EventsPostback> {
    return new Promise((resolve, reject) => {
      axios
        .post<Events>(
          configService.personaleOrAdminUrl("events/delete_recurrent_events_on_dates.json"),
          {
            recurrent_event_id: events.recurrent_event_id,
            dates: events.dates
              .map((d) => {
                return convertDateOnlyToUtc(d);
              })
              .join(",")
          }
        )
        .then((response: AxiosResponse) => {
          resolve(response.data as any);
        })
        .catch((error) => reject(error));
    });
  }

  async fetchEvents(fra: Date, tilOgMed: Date): Promise<Events[]> {
    return new Promise((resolve, reject) => {
      const utcFra: string = convertDateOnlyToUtc(fra);
      const utcTilOgMed: string = convertDateOnlyToUtc(tilOgMed);

      axios
        .get<Events[]>(configService.personaleOrAdminUrl("events.json"), {
          params: {
            institution_id: configService.institutionId(),
            start_date: utcFra,
            end_date: utcTilOgMed
          }
        })
        .then((response: AxiosResponse) => {
          resolve(Events.fromJSONArray(ArrayFromEnvelope<Events>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async fetchEventsByRecurrenceId(recurrenceId: number): Promise<Events[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<Events[]>(configService.personaleOrAdminUrl("events.json"), {
          params: {
            institution_id: configService.institutionId(),
            recurrent_event_id: recurrenceId
          }
        })
        .then((response: AxiosResponse) => {
          resolve(Events.fromJSONArray(ArrayFromEnvelope<Events>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async fetchEventParticipations(
    employeeIdList: number[],
    fra: Date,
    tilOgMed: Date
  ): Promise<EventParticipations[]> {
    return new Promise((resolve, reject) => {
      const utcFra: string = convertDateTimeToUtc(fra);
      const utcTilOgMed: string = convertDateTimeToUtc(tilOgMed);

      axios
        .get<EventParticipations[]>(configService.APIInstitutionUrl("event_participations.json"), {
          params: {
            employee_id_list: employeeIdList.join(","),
            start_time: utcFra,
            end_time: utcTilOgMed
          }
        })
        .then((response: AxiosResponse) => {
          resolve(
            EventParticipations.fromJSONArray(ArrayFromEnvelope<EventParticipations>(response.data))
          );
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async fetchEventParticipationsByEventId(eventId: number): Promise<EventParticipations[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<EventParticipations[]>(configService.APIInstitutionUrl("event_participations.json"), {
          params: {
            event_id: eventId
          }
        })
        .then((response: AxiosResponse) => {
          resolve(
            EventParticipations.fromJSONArray(ArrayFromEnvelope<EventParticipations>(response.data))
          );
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async fetchEventTypes(): Promise<EventTypes[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<EventTypes[]>(configService.APIInstitutionUrl("event_types.json"), {
          params: {
            institution_id: configService.institutionId()
          }
        })
        .then((response: AxiosResponse) => {
          resolve(EventTypes.fromJSONArray(ArrayFromEnvelope<EventTypes>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async updateEventTypes(eventTypes: EventTypes): Promise<EventTypes> {
    return new Promise((resolve, reject) => {
      axios
        .put<EventTypes>(
          configService.APIInstitutionUrl("event_types/" + eventTypes.id + ".json"),
          {
            event_type: eventTypes.convertToPutObject()
          }
        )
        .then((response: AxiosResponse) => {
          resolve(EventTypes.fromJSON(ObjectFromEnvelope<EventTypes>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }

  async createEventType(eventType: EventTypes): Promise<EventTypes> {
    return new Promise((resolve, reject) => {
      axios
        .post<EventTypes>(configService.APIInstitutionUrl("event_types.json"), {
          event_type: eventType
        })
        .then((response: AxiosResponse) => {
          resolve(EventTypes.fromJSON(ObjectFromEnvelope<EventTypes>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }

  async deleteEventType(eventType: EventTypes): Promise<EventTypes> {
    return new Promise((resolve, reject) => {
      axios
        .delete<EventTypes>(
          configService.APIInstitutionUrl("event_types/" + eventType.id + ".json")
        )
        .then(() => {
          resolve(eventType);
        })
        .catch((error) => reject(error));
    });
  }
}

export const eventsService: EventsService = new EventsService();
